import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/context/tenant-context";
import { useAgentAccess } from "@/hooks/use-agent-access";
import { agentTasksApi, type AgentTask } from "@/api/agent-tasks";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { NewTaskForm } from "@/components/tasks/new-task-form";
import { TaskDetailDrawer } from "@/components/tasks/task-detail-drawer";
import { TASKS_QUERY_ROOT, TaskList } from "@/components/tasks/task-list";

type Filter = "all" | "recurring" | "one_off";

// ── Sync button ──────────────────────────────────────────────────────
function SyncButton({ agentId }: { agentId: string }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<{ created: number; updated: number; completed: number } | null>(null);

  const syncMutation = useMutation({
    mutationFn: () => agentTasksApi.syncFromAgent(agentId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: TASKS_QUERY_ROOT });
      setResult(data);
      setTimeout(() => setResult(null), 5000);
    },
  });

  return (
    <div className="flex items-center gap-2">
      {result && (
        <span className="text-[11px] text-text-muted">
          {result.created > 0 ? `+${result.created}` : ""}
          {result.updated > 0 ? ` ~${result.updated}` : ""}
          {result.created === 0 && result.updated === 0 ? "Up to date" : " synced"}
        </span>
      )}
      <Button
        size="sm"
        variant="outline"
        disabled={syncMutation.isPending}
        onClick={() => syncMutation.mutate()}
      >
        <RefreshCw size={12} className={cn("mr-1", syncMutation.isPending && "animate-spin")} />
        {syncMutation.isPending ? "Syncing..." : "Sync from Agent"}
      </Button>
    </div>
  );
}

/** Recurring / One-Off / All filter, shared look with the popup Tasks page. */
export function TaskTypeFilter({
  value,
  onChange,
  tasks,
}: {
  value: Filter;
  onChange: (f: Filter) => void;
  tasks: AgentTask[];
}) {
  return (
    <div className="flex gap-1">
      {(
        [
          ["all", "All"],
          ["recurring", "Recurring"],
          ["one_off", "One-Off"],
        ] as const
      ).map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={cn(
            "rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors",
            value === key ? "bg-accent/10 text-accent" : "text-text-muted hover:text-text-primary",
          )}
        >
          {label}
          <span className="ml-1 opacity-60">
            {key === "all" ? tasks.length : tasks.filter((t) => t.type === key).length}
          </span>
        </button>
      ))}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────
export function AgentTasksTab({ agentId }: { agentId: string }) {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const { canManage } = useAgentAccess(agentId);
  const [filter, setFilter] = useState<Filter>("all");
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTask, setSelectedTask] = useState<AgentTask | null>(null);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: queryKeys.agentTasks.byAgent(agentId),
    queryFn: () => agentTasksApi.list(tenantId, { agentId }),
  });

  // Auto-sync on mount for OpenClaw agents: their cron jobs live on the VM
  // and don't land in agent_tasks until syncFromAgent is called. The server
  // no-ops this for Native agents, so firing unconditionally is safe. We
  // key the ref on agentId so switching between agents re-fires once each.
  const lastSyncedAgentId = useRef<string | null>(null);
  useEffect(() => {
    if (lastSyncedAgentId.current === agentId) return;
    lastSyncedAgentId.current = agentId;
    agentTasksApi
      .syncFromAgent(agentId)
      .then(() => queryClient.invalidateQueries({ queryKey: TASKS_QUERY_ROOT }))
      .catch(() => {
        // Non-fatal: user still sees whatever's already in agent_tasks,
        // and the manual "Sync from Agent" button is still available.
      });
  }, [agentId, queryClient]);

  const filtered = filter === "all" ? tasks : tasks.filter((t) => t.type === filter);

  return (
    // Same frame as the Access tab: p-6 inset, a heading row with the actions
    // on the right, then the list.
    <div className="h-full space-y-3 overflow-y-auto p-6">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-medium text-text-primary">Tasks</h3>
        <TaskTypeFilter value={filter} onChange={setFilter} tasks={tasks} />
        <div className="ml-auto flex items-center gap-2">
          {canManage && <SyncButton agentId={agentId} />}
          {canManage && (
            <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
              <Plus size={14} className="mr-1" />
              New Task
            </Button>
          )}
        </div>
      </div>

      {showCreate && <NewTaskForm agentId={agentId} onDone={() => setShowCreate(false)} />}

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading...</p>
      ) : (
        <TaskList
          tasks={filtered}
          onOpen={setSelectedTask}
          emptyText={
            filter === "all"
              ? "No tasks yet."
              : `No ${filter === "recurring" ? "recurring" : "one-off"} tasks.`
          }
        />
      )}

      <TaskDetailDrawer task={selectedTask} tenantId={tenantId} onClose={() => setSelectedTask(null)} />
    </div>
  );
}
